import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Product } from '../types/catalog';
interface CartLine {
  product: Product;
  quantity: number;
}
interface CartValue {
  lines: CartLine[];
  count: number;
  total: number;
  isOpen: boolean;
  add: (product: Product) => void;
  remove: (id: string) => void;
  setQuantity: (id: string, quantity: number) => void;
  open: () => void;
  close: () => void;
}
const CartContext = createContext<CartValue | null>(null);
export function CartProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [lines, setLines] = useState<CartLine[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const add = useCallback((product: Product) => {
    setLines(current => {
      const existing = current.find(line => line.product.id === product.id);
      if (existing) {
        return current.map(line => line.product.id === product.id ? {
          ...line,
          quantity: line.quantity + 1
        } : line);
      }
      return [...current, {
        product,
        quantity: 1
      }];
    });
    setIsOpen(true);
  }, []);
  const remove = useCallback((id: string) => {
    setLines(current => current.filter(line => line.product.id !== id));
  }, []);
  const setQuantity = useCallback((id: string, quantity: number) => {
    setLines(current => quantity <= 0 ? current.filter(line => line.product.id !== id) : current.map(line => line.product.id === id ? {
      ...line,
      quantity
    } : line));
  }, []);
  const value = useMemo<CartValue>(() => {
    const count = lines.reduce((sum, line) => sum + line.quantity, 0);
    const total = lines.reduce((sum, line) => sum + line.quantity * line.product.price, 0);
    return {
      lines,
      count,
      total,
      isOpen,
      add,
      remove,
      setQuantity,
      open: () => setIsOpen(true),
      close: () => setIsOpen(false)
    };
  }, [lines, isOpen, add, remove, setQuantity]);
  return <CartContext.Provider value={value} data-id="element-117" data-mp="6tx1xgmnpms7">{children}</CartContext.Provider>;
}
export function useCart(): CartValue {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart doit être utilisé dans un CartProvider');
  }
  return context;
}
export const formatPrice = (value: number): string => new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: 'EUR'
}).format(value);